import { inject, Injectable, Injector } from '@angular/core';
import { HttpHandler, HttpRequest, HttpEvent, HttpResponse, HttpEventType, HttpParams } from '@angular/common/http';
import { map, Observable, filter } from 'rxjs';
import { RecaptchaInterceptor } from './recaptcha.interceptor';
import { RecaptchaService } from './recaptcha.service';

@Injectable({
  providedIn: 'root'
})
export class RecaptchaHttpClient {
  private interceptor: RecaptchaInterceptor;
  private handler: HttpHandler = inject(HttpHandler);
  private injector: Injector = inject(Injector);
  constructor() {
    this.interceptor = new RecaptchaInterceptor(this.injector.get(RecaptchaService));
  }

  get<T>(
    url: string,
    params?:
      | HttpParams
      | { [param: string]: string | number | boolean | readonly (string | number | boolean)[] }
      | undefined,
    userID: string | null = null
  ): Observable<T> {
    let httpParams: HttpParams | undefined;
    if (params && !(params instanceof HttpParams)) {
      httpParams = new HttpParams({ fromObject: params });
    } else {
      httpParams = params;
    }

    const req = new HttpRequest('GET', url, { params: httpParams });
    return this.handleRequest(req, userID);
  }

  post<T>(url: string, body: unknown, userID: string | null = null, options?: Record<string, unknown>): Observable<T> {
    const req = new HttpRequest('POST', url, body, options);
    return this.handleRequest(req, userID);
  }

  put<T>(url: string, body: unknown, userID: string | null = null, options?: Record<string, unknown>): Observable<T> {
    const req = new HttpRequest('PUT', url, body, options);
    return this.handleRequest(req, userID);
  }

  delete<T>(url: string, body?: unknown, userID: string | null = null): Observable<T> {
    const req = new HttpRequest('DELETE', url, body);
    return this.handleRequest(req, userID);
  }

  private createHandlerChain(userID: string | null): HttpHandler {
    return {
      handle: (req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> => {
        return this.interceptor.intercept(req, this.handler, userID);
      }
    };
  }

  private handleRequest<T>(req: HttpRequest<unknown>, userID: string | null): Observable<T> {
    const handlerChain = this.createHandlerChain(userID);
    return handlerChain.handle(req).pipe(
      filter((event) => event.type === HttpEventType.Response),
      map((event) => {
        if (event instanceof HttpResponse) {
          return event.body as T;
        }
        throw new Error('Unexpected event type');
      })
    );
  }
}
